import {
  DollarCircleOutlined,
  WarningOutlined,
  AreaChartOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  FireOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { Callout, HelpParagraph, HeroBlock, KeyValueList, RelatedTopics, SectionTitle } from '@/features/help/HelpPrimitives';

// Help guides for Agent Studio → Watch → Costs. One per section, each answering:
// what the number counts · how something lands here · what it does NOT mean · what to do.

const related = (
  <RelatedTopics
    items={[
      { topic: 'costs.coverage', label: 'Is the number complete?', icon: <WarningOutlined /> },
      { topic: 'costs.per-run', label: 'Cost per run', icon: <PlayCircleOutlined /> },
      { topic: 'costs.unusual', label: 'Unusual runs', icon: <FireOutlined /> },
      { topic: 'models.overview', label: 'Models', icon: <CodeOutlined /> },
    ]}
  />
);

export const CostsHelpBody = () => (
  <>
    <HeroBlock
      icon={<DollarCircleOutlined />}
      title="Costs"
      tagline="What every AI call cost — by day, by agent and by model — and which calls are missing a price."
      accent="amber"
    />
    <HelpParagraph>
      Every call an agent makes to a language model writes one row to <code>ai.PromptLog</code> with its
      token counts. At that moment the cost is worked out from the model's rate (Model pricing) and stored
      on the row. This page adds up those stored costs. It never re-prices an old call and never guesses a
      price that was not stored.
    </HelpParagraph>
    <SectionTitle>Reading the page</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Range', value: 'Calendar days in UTC, both ends included. "30 days" ends today (UTC).' },
        { key: 'Merchant', value: 'All merchants by default — this is a platform page. Pick one to see only its calls.' },
        { key: 'Spend', value: 'The sum of stored costs. When the yellow strip shows, some calls carry no cost and spend is a floor.' },
        { key: 'Run', value: 'One request: a chat turn, or one pass of a workflow such as reading a referral. A run can make many calls.' },
        { key: 'Export CSV', value: 'Day × merchant × agent × model, with the calls each line could not price — for finance reconciliation.' },
      ]}
    />
    <Callout tone="info" title="Why the numbers here can differ from Insights">
      Insights counts a call with no price as $0. This page leaves it out of spend and counts it in the
      yellow strip instead, so a gap is never mistaken for a free call.
    </Callout>
    <SectionTitle>Related</SectionTitle>
    {related}
  </>
);

export const CostsCoverageHelpBody = () => (
  <>
    <HeroBlock icon={<WarningOutlined />} title="Is the number complete?" tagline="Calls that carry no usable cost — and why." accent="amber" />
    <SectionTitle>What it counts</SectionTitle>
    <KeyValueList
      items={[
        { key: 'No rate on file', value: 'When the call was written, Model pricing had no active rate for that model name, so no cost was stored. gpt-4o-mini has never had one.' },
        { key: 'Rate added since', value: 'A rate exists today but the call is older than it, or the rate did not match (rates are looked up as provider "OpenAI", so an Anthropic rate row is never found).' },
        { key: 'Stored as $0.00', value: 'Tokens were used but the stored cost is zero. Before 7 Sep 2026 costs were rounded to whole cents, so any call under half a cent became $0.00.' },
      ]}
    />
    <SectionTitle>What it does not mean</SectionTitle>
    <HelpParagraph>
      These calls were not free, and the page does not estimate them. A failed call that used no tokens
      is not counted here — it genuinely cost nothing.
    </HelpParagraph>
    <SectionTitle>What to do</SectionTitle>
    <HelpParagraph>
      Add the missing rate in <b>Model pricing</b>. New calls are priced from then on (after the
      5-minute rate cache expires). Older calls keep their stored value — history is not re-priced.
    </HelpParagraph>
  </>
);

export const CostsTrendHelpBody = () => (
  <>
    <HeroBlock icon={<AreaChartOutlined />} title="Spend over time" tagline="Stored spend per day, week or month, stacked by model." accent="amber" />
    <KeyValueList
      items={[
        { key: 'A column', value: 'All priced calls whose time falls in that UTC day (weeks start Monday; months on the 1st). Hover for the split by model.' },
        { key: 'An empty column', value: 'No priced calls that day — either no traffic, or every call that day is in the yellow strip.' },
        { key: 'A spike', value: 'Usually a batch: the referral intake test corpus, a validation run, a backfill. Open Top agents for the same range to see who.' },
      ]}
    />
  </>
);

export const CostsAgentsHelpBody = () => (
  <>
    <HeroBlock icon={<RobotOutlined />} title="Top agents by spend" tagline="Which agents cost the most, and what a typical run of each costs." accent="amber" />
    <KeyValueList
      items={[
        { key: 'Spend · share', value: 'Stored spend for the agent and its share of the page total.' },
        { key: 'Runs', value: "Requests the agent took part in. One referral read is one run for each agent it uses." },
        { key: 'Per run · median, P95', value: 'Half of the agent\'s runs cost less than the median; 95 in 100 cost less than P95. Shown only once the agent has 10 fully priced runs in the range.' },
        { key: 'Unusual', value: 'Runs that cost more than 5× that agent\'s own median.' },
        { key: 'No agent recorded', value: 'Calls written without an agent id — usually platform jobs.' },
      ]}
    />
    <HelpParagraph>Click a row to see the agent's days, its models and its costliest runs.</HelpParagraph>
  </>
);

export const CostsPerRunHelpBody = () => (
  <>
    <HeroBlock icon={<PlayCircleOutlined />} title="Cost per run" tagline="How much one request costs, and how the costs are spread." accent="amber" />
    <HelpParagraph>
      A run is every call that shares one request id: a chat turn, or one pass of a workflow. Only runs
      whose every call carries a cost are counted here — a run with a missing price would look cheaper
      than it was. The number left out is shown under the chart.
    </HelpParagraph>
    <Callout tone="info" title="Why not cost per conversation?">
      Calls do not record which conversation they belong to (the thread id is empty on nearly every call),
      so a conversation cannot be added up yet. A run can.
    </Callout>
    <HelpParagraph>
      Red bands are the tail at 25¢ and above — where retry storms, tool-call loops and very large
      documents show up.
    </HelpParagraph>
  </>
);

export const CostsUnusualHelpBody = () => (
  <>
    <HeroBlock icon={<FireOutlined />} title="Unusual runs" tagline="Runs that cost far more than that agent usually costs." accent="red" />
    <SectionTitle>How a run lands here</SectionTitle>
    <HelpParagraph>
      Its cost is more than 5× the agent's median run in the same range. Each agent is compared with
      itself — a 30¢ referral read is normal for the extractor and alarming for a chat companion. An agent
      needs 10 fully priced runs before it is judged at all.
    </HelpParagraph>
    <SectionTitle>What it does not mean</SectionTitle>
    <HelpParagraph>Not an error. A very long document or a deliberate deep read can be legitimately expensive.</HelpParagraph>
    <SectionTitle>What to do</SectionTitle>
    <HelpParagraph>
      Open the run: many calls with few tool calls suggests retries; many tool calls suggests a loop; one
      huge call suggests an oversized prompt or document. This list is shown on the page — nothing is
      emailed yet.
    </HelpParagraph>
  </>
);
